/** Global dashboard filters (PROMPT §10) with URL state (deep links). */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  isKpiId,
  isSeccionId,
  sortKpis,
  sortSecciones,
  KPI_IDS,
  SECCION_IDS,
  type KpiId,
  type SeccionId,
} from './vista.js';
import { DEFAULT_TIMEFRAME, parseTimeframe, type Timeframe } from './timeframe.js';

export type Currency = 'USD' | 'BS';
export type { Timeframe };

export interface DashboardFilters {
  from: string;
  to: string;
  sedes: number[]; // empty = all
  currency: Currency;
  compare: boolean;
  timeframe: Timeframe; // ventana + tamaño de vela de los gráficos
  kpis: KpiId[]; // tarjetas KPI visibles (orden del catálogo); [] = ninguna
  secciones: SeccionId[]; // áreas visibles (gráficos, tablas, paneles); [] = ninguna
}

function todayLocalIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function defaultFilters(): DashboardFilters {
  const today = todayLocalIso();
  return {
    from: `${today.slice(0, 8)}01`,
    to: today,
    sedes: [],
    currency: 'USD',
    compare: true,
    timeframe: DEFAULT_TIMEFRAME,
    kpis: [...KPI_IDS],
    secciones: [...SECCION_IDS],
  };
}

export function filtersFromSearch(sp: URLSearchParams): DashboardFilters {
  const d = defaultFilters();
  const sedes = sp.get('sedes');
  const tf = sp.get('tf');
  // Ausente = todo (el defecto). Presente y vacío ('kpis=') = nada; hay que
  // distinguirlos o no habría forma de deep-linkear «sin tarjetas».
  const kpis = sp.get('kpis');
  const secc = sp.get('secc');
  return {
    from: sp.get('from') ?? d.from,
    to: sp.get('to') ?? d.to,
    sedes: sedes ? sedes.split(',').map(Number).filter(Number.isFinite) : [],
    currency: sp.get('cur') === 'BS' ? 'BS' : 'USD',
    compare: sp.get('compare') !== 'false',
    timeframe: tf == null ? d.timeframe : parseTimeframe(tf),
    kpis: kpis == null ? d.kpis : sortKpis(kpis.split(',').filter(isKpiId)),
    secciones: secc == null ? d.secciones : sortSecciones(secc.split(',').filter(isSeccionId)),
  };
}

export function filtersToSearch(f: DashboardFilters): URLSearchParams {
  const sp = new URLSearchParams();
  sp.set('from', f.from);
  sp.set('to', f.to);
  if (f.sedes.length) sp.set('sedes', f.sedes.join(','));
  sp.set('cur', f.currency);
  if (!f.compare) sp.set('compare', 'false');
  if (f.timeframe !== DEFAULT_TIMEFRAME) sp.set('tf', f.timeframe);
  // Solo se serializan si no están todas: la URL del caso normal no cambia.
  if (f.kpis.length !== KPI_IDS.length) sp.set('kpis', f.kpis.join(','));
  if (f.secciones.length !== SECCION_IDS.length) sp.set('secc', f.secciones.join(','));
  return sp;
}

/** Reactive filters bound to the URL query string. */
export function useFilters(): [DashboardFilters, (patch: Partial<DashboardFilters>) => void] {
  const [sp, setSp] = useSearchParams();
  const filters = useMemo(() => filtersFromSearch(sp), [sp]);
  const update = useCallback(
    (patch: Partial<DashboardFilters>) => {
      setSp(filtersToSearch({ ...filters, ...patch }), { replace: true });
    },
    [filters, setSp],
  );
  return [filters, update];
}

/** Comma-separated sede ids for API params (undefined = all). */
export function sedesParam(f: DashboardFilters): string | undefined {
  return f.sedes.length ? f.sedes.join(',') : undefined;
}
